import { useEffect, useState } from "react";

import {
  buildPath,
  buildTemplatePath,
  Button,
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  pn,
  SearchBar,
  Stack,
  Typography,
} from "@compass/core";

import { ListFooter } from "components";
import {
  Permission,
  PermissionAction,
  StudentEnrollmentListDto,
  StudentFilter as StudentFilterModel,
  StudentEnrollmentListResource,
  StudentEnrollmentFilter as StudentEnrollmentFilterModel,
  StudentEnrollmentListFilter,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import { StudentFilter } from "pages/students/students/StudentFilter";
import { Outlet, useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { StudentEnrollmentFilter } from "../StudentEnrollmentFilter";
import { studentEnrollmentPageRoute } from "../StudentEnrollmentPage/StudentEnrollmentPage";
import { StudentEnrollmentTable } from "./StudentEnrollmentTable";

const routeName = "studentEnrollments";

export const studentEnrollmentListPageRoute: PageRoute = {
  handle: "Enrollments",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const StudentEnrollmentListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.StudentEnrollment;
  const navigate = useNavigate();

  const { listFilter, setListFilter } = useInitListFilter<StudentEnrollmentListFilter>();
  const [initListFilter, setInitListFilter] = useState<StudentEnrollmentListFilter>(listFilter);
  const [listItems, setListItems] = useState<StudentEnrollmentListDto[]>([]);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<StudentEnrollmentListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const {
    tableContainerRef,
    applyPage,
    applyPageSize,
    applySortMethod,
    applySort,
    applySearch,
    loadList,
    selectId,
    selectedIds,
    setSelectedIds,
  } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.studentEnrollments.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.studentEnrollments.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    ApiService.studentEnrollments.initListFilter().then((response) => {
      setListFilter(response);
      setInitListFilter(response);
      loadList(response);
    });

    fetchResource();
  }, []);

  const fetchResource = () => {
    ApiService.studentEnrollments.getListResource().then((response) => {
      setResource(response);
    });
  };

  const listFilterNode = useListFilter<StudentEnrollmentListFilter>({
    maxWidth: "xl",
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <Stack spacing="lg">
        <Stack spacing="lg">
          <Typography variant="h6" fontWeight={(theme) => theme.typography.fontWeightRegular}>
            Student:
          </Typography>

          <StudentFilter
            filter={listFilter.studentFilter || ({} as StudentFilterModel)}
            setFilter={(filter) =>
              setListFilter(
                produce(listFilter, (draft) => {
                  draft.studentFilter = filter;
                })
              )
            }
          />
        </Stack>

        <Stack spacing="lg">
          <Typography variant="h6" fontWeight={(theme) => theme.typography.fontWeightRegular}>
            Enrollment:
          </Typography>

          <StudentEnrollmentFilter
            filter={listFilter.enrollmentFilter || ({} as StudentEnrollmentFilterModel)}
            setFilter={(filter) =>
              setListFilter(
                produce(listFilter, (draft) => {
                  draft.enrollmentFilter = filter;
                })
              )
            }
          />
        </Stack>
      </Stack>
    ),
  });

  const getIdsMethods = () => ApiService.studentEnrollments.getIds(listFilter);

  return (
    <>
      <Page
        header={
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="inherit">{studentEnrollmentListPageRoute.handle}</Typography>

            <Stack direction="row" alignItems="center" spacing="sm">
              {isAuthorized(ROOT_PERMISSION, PermissionAction.Create) && (
                <Button
                  onClick={() =>
                    navigate({
                      pathname: studentEnrollmentPageRoute.getPath({ id: 0, crudMode: "edit", screenType: "standard" }),
                    })
                  }
                  variant="outlined"
                >
                  + New
                </Button>
              )}
            </Stack>
          </Stack>
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <StudentEnrollmentTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              studentEnrollments={listItems}
              resource={resource}
              listTotal={total}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              searchFocused={searchFocused}
              selectId={selectId}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              getIdsPromise={() => getIdsMethods()}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider
        value={{
          loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }),
          clearSelectedIds: () => setSelectedIds([]),
        }}
      >
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
